import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Check, CheckCircle2, CreditCard, Loader2, Sparkles, XCircle } from "lucide-react";
import { SmsUsageGauge } from "@/components/parent-communication/SmsUsageGauge";

const MONTHLY_LINK = "https://square.link/u/fC3bVc1z";  // Family SMS Access Portal Monthly
const ANNUAL_LINK  = "https://square.link/u/AvBGNgaT";  // Family SMS Access Portal Yearly

export const ParentCenterSubscriptionCard = ({ settings, selectedPlan, onPlanChange, ownerEmail, onCancelSubscription, cancelling }) => {
  const isActive = settings.paymentOptions.centerSubscriptionActive;
  const planLabel = selectedPlan === "annual" ? "$269.89/year" : "$24.99/month";
  const paymentLink = selectedPlan === "annual" ? ANNUAL_LINK : MONTHLY_LINK;

  const handlePayNow = () => {
    window.open(paymentLink, "_blank", "noopener,noreferrer");
  };

  return (
    <Card className="border-0 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 shadow-xl ring-1 ring-cyan-100" data-testid="parent-center-subscription-card">
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="bg-cyan-100 text-cyan-800" data-testid="parent-center-subscription-badge">Center activation first</Badge>
          <Badge className="bg-emerald-100 text-emerald-700" data-testid="parent-center-subscription-price-badge">$24.99 monthly • $269.89 yearly (10% off)</Badge>
        </div>
        <CardTitle className="text-2xl text-slate-900">Activate Parent Communication for your center</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Show live usage gauge for subscribers */}
        {isActive && (
          <div data-testid="parent-center-subscription-usage">
            <SmsUsageGauge />
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <button type="button" onClick={() => onPlanChange("monthly")} className={`rounded-[1.6rem] border p-5 text-left transition ${selectedPlan === "monthly" ? "border-cyan-500 bg-cyan-50 shadow" : "border-slate-200 bg-white hover:border-cyan-300"}`} data-testid="parent-center-plan-monthly">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700">Monthly</p>
                <p className="mt-3 text-3xl font-bold text-slate-900">$24.99<span className="text-base font-normal text-slate-500">/month</span></p>
              </div>
              <div className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${selectedPlan === "monthly" ? "border-cyan-500 bg-cyan-500" : "border-slate-300"}`}>
                {selectedPlan === "monthly" && <Check className="h-3 w-3 text-white" />}
              </div>
            </div>
          </button>

          <button type="button" onClick={() => onPlanChange("annual")} className={`rounded-[1.6rem] border p-5 text-left transition ${selectedPlan === "annual" ? "border-emerald-500 bg-emerald-50 shadow" : "border-slate-200 bg-white hover:border-emerald-300"}`} data-testid="parent-center-plan-annual">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">Yearly</p>
                <p className="mt-3 text-3xl font-bold text-slate-900"><span className="line-through text-slate-400 text-xl mr-2">$299.88</span>$269.89<span className="text-base font-normal text-slate-500">/year</span></p>
                <p className="mt-2 text-sm font-medium text-emerald-700">Save $29.99 — 10% off at checkout</p>
              </div>
              <div className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${selectedPlan === "annual" ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`}>
                {selectedPlan === "annual" && <Check className="h-3 w-3 text-white" />}
              </div>
            </div>
          </button>
        </div>

        {ownerEmail && (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4" data-testid="parent-center-email-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
              <div>
                <p className="text-sm font-bold text-emerald-800">Important — use this email at Square checkout</p>
                <p className="mt-1 text-sm text-emerald-700">When prompted for an email on Square, you <span className="font-bold">MUST</span> enter:</p>
                <p className="mt-2 rounded-lg bg-white px-3 py-2 text-sm font-bold tracking-wide text-emerald-900 ring-1 ring-emerald-300" data-testid="parent-center-owner-email">{ownerEmail}</p>
                <p className="mt-2 text-xs text-emerald-600">This is how your subscription is linked to your account. Using a different email will delay activation.</p>
              </div>
            </div>
          </div>
        )}

        <Button onClick={handlePayNow} className="w-full rounded-full bg-gradient-to-r from-cyan-600 to-emerald-600 py-6 text-lg font-semibold text-white hover:from-cyan-700 hover:to-emerald-700" data-testid="parent-center-subscribe-button">
          <CreditCard className="mr-2 h-5 w-5" /> Pay {planLabel} via Square
        </Button>
        <p className="text-center text-sm text-slate-500">After completing payment on Square, return here and click the <span className="font-semibold">refresh button at the top of the page</span> to activate your account.</p>

        {isActive ? (
          <div className="rounded-[1.6rem] border-2 border-cyan-400 bg-cyan-50 p-4" data-testid="parent-center-active-state">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-cyan-600" />
              <p className="flex-1 text-sm font-semibold text-cyan-800">Parent Communication is active</p>
              <Button onClick={onCancelSubscription} disabled={cancelling} variant="outline" size="sm" className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="parent-center-cancel-subscription-button">
                {cancelling ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Cancelling...</> : <><XCircle className="mr-2 h-4 w-4" /> Cancel at anytime</>}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-center text-sm text-slate-500" data-testid="parent-center-subscription-help">Secure checkout powered by Square. The center must activate this add-on before parent billing and trials unlock.</p>
        )}

        <div className="rounded-[1.6rem] border-2 border-cyan-400 bg-cyan-50 p-4" data-testid="parent-center-revenue-opportunity">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 text-cyan-600" />
            <div>
              <p className="font-semibold text-cyan-800">Revenue opportunity</p>
              <p className="mt-1 text-sm text-cyan-700">After center activation, you can set your own per-child monthly or yearly fee, pass a trial to parents, and require payment after the trial ends.</p>
            </div>
          </div>
        </div>

        <div className="rounded-[1.6rem] border-2 border-emerald-400 bg-emerald-50 p-4" data-testid="parent-center-fair-use-policy">
          <p className="font-bold text-emerald-800 mb-1.5">Messaging Fair Use Policy</p>
          <p className="text-sm text-emerald-900 leading-relaxed mb-2">
            Your $24.99/month plan includes generous messaging designed to easily support everyday childcare communication, such as parent updates, reminders, alerts, menus, children's activities and important notifications, etc. If your center happens to reach higher messaging levels, no worries&mdash;just reach out to us, and we'll help you continue sending messages for the rest of the month with a simple bulk pricing option.
          </p>
          <p className="text-sm text-emerald-900 leading-relaxed">
            This plan is built for regular day-to-day communication and isn't intended for large-scale automated marketing. If you need higher-volume messaging, we're happy to work with you&mdash;just contact us at <a href="mailto:admin@simplesignin.com" className="underline font-semibold">admin@simplesignin.com</a> for options.
          </p>
        </div>

      </CardContent>
    </Card>
  );
};
